import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSignupWizard } from './signup-wizard-context';
import WizardStep from './wizard-step';
import { useStepNavigation } from './use-step-navigation';
import { isValidEmail } from '../../util';
import Field from '../../components/shared/Field/Field';
import StyledCheckbox from '../../components/shared/StyledCheckbox/StyledCheckbox';

const StepAboutYou: FC = () => {
  const { t } = useTranslation('newSupplier', { keyPrefix: 'wizard' });
  const { state } = useSignupWizard();

  const [name, setName] = useState(state.name || '');
  const [title, setTitle] = useState(state.title || '');
  const [phoneNumber, setPhoneNumber] = useState(state.phoneNumber || '');
  const [email, setEmail] = useState(state.email || '');
  const [password, setPassword] = useState(state.password || '');
  const [addAsContact, setAddAsContact] = useState(state.addAsContact ?? true);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) newErrors.name = t('aboutYou.errors.nameRequired', 'Namn krävs');
    if (!email.trim()) {
      newErrors.email = t('aboutYou.errors.emailRequired', 'E-post krävs');
    } else if (!isValidEmail(email.trim())) {
      newErrors.email = t('aboutYou.errors.emailInvalid', 'Ange en giltig e-postadress');
    }
    if (!password) {
      newErrors.password = t('aboutYou.errors.passwordRequired', 'Lösenord krävs');
    } else if (password.length < 8) {
      newErrors.password = t('aboutYou.errors.passwordMinLength', 'Lösenordet måste vara minst 8 tecken');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const canProceed =
    name.trim().length > 0 &&
    email.trim().length > 0 &&
    isValidEmail(email.trim()) &&
    password.length >= 8;

  const { handleNext, handleBack } = useStepNavigation({
    currentStep: 4,
    nextPath: '/supplier-sign-up/invite',
    prevPath: '/supplier-sign-up/places',
    validate,
    eventName: 'signup_wizard_step_about_you',
    onBeforeNext: () => ({
      name: name.trim(),
      title: title.trim(),
      phoneNumber: phoneNumber.trim(),
      email: email.trim(),
      password,
      addAsContact,
    }),
  });

  return (
    <WizardStep
      step={4}
      title={t('aboutYou.title', 'Vem är du?')}
      description={t('aboutYou.description', 'Dina uppgifter')}
      onNext={handleNext}
      onBack={handleBack}
      canProceed={canProceed}
    >
      {/* Name */}
      <Field
        id="name"
        value={name}
        onChange={(val) => {
          setName(val);
          if (errors.name) setErrors(prev => ({ ...prev, name: '' }));
        }}
        placeholder="Förnamn Efternamn"
        label={t('aboutYou.name', 'Namn')}
        required
        errorMessage={errors.name}
      />

      {/* Title */}
      <Field
        id="title"
        value={title}
        onChange={(val) => {
          setTitle(val);
        }}
        placeholder={t('aboutYou.titlePlaceholder', 't.ex. Projektledare')}
        label={t('aboutYou.titleLabel', 'Titel')}
      />

      {/* Phone Number */}
      <Field
        id="phone-number"
        type="tel"
        value={phoneNumber}
        onChange={(val) => {
          setPhoneNumber(val);
        }}
        placeholder="+46 70 000 00 00"
        label={t('aboutYou.phone', 'Telefonnummer')}
      />

      {/* Email */}
      <Field
        id="email"
        type="email"
        value={email}
        onChange={(val) => {
          setEmail(val);
          if (errors.email) setErrors(prev => ({ ...prev, email: '' }));
        }}
        placeholder="namn@företag.se"
        label={t('aboutYou.email', 'E-post')}
        required
        errorMessage={errors.email}
      />

      {/* Password */}
      <Field
        id="password"
        type="password"
        value={password}
        onChange={(val) => {
          setPassword(val);
          if (errors.password) setErrors(prev => ({ ...prev, password: '' }));
        }}
        placeholder={t('aboutYou.passwordPlaceholder', 'Minst 8 tecken')}
        label={t('aboutYou.password', 'Lösenord')}
        required
        errorMessage={errors.password || (!errors.password && password.length === 0 ? '' : undefined)}
      />
      {!errors.password && (
        <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '-1.25rem' }}>
          {t('aboutYou.passwordHint', 'Lösenordet måste vara minst 8 tecken')}
        </p>
      )}

      {/* Contact person checkbox */}
      <label className="flex items-center gap-3 cursor-pointer select-none tracking-normal">
        <StyledCheckbox checked={addAsContact} onChange={() => setAddAsContact(!addAsContact)} />
        <span style={{ fontSize: '0.875rem', color: 'var(--gray-700)' }}>
          {t('aboutYou.addAsContact', 'Visa mig som kontaktperson i företagspresentationen')}
        </span>
      </label>
    </WizardStep>
  );
};

export default StepAboutYou;
